import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import {
  type CustomAccountingIntegration,
  useGetCustomAccountingIntegration,
} from '@omnea-app/fe-organisation-integrations/useGetCustomAccountingIntegration'
import {
  type OrganisationIntegration,
  useListOrganisationIntegrations,
} from '@omnea-app/fe-organisation-integrations/useListOrganisationIntegrations'
import {
  AccountingIntegration,
  globalIntegrationsMetadata,
  isAccountingIntegration,
} from '@omnea-app/packages/universal/integrations/globalIntegrations'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'

interface AccountingIntegrationInfo {
  hasAccountingIntegration: boolean
  integration: { name: string; type: AccountingIntegration } | null
  supportsAddresses: boolean
  isLoading: boolean
}

export type AccountingIntegrationRef = NonNullable<AccountingIntegrationInfo['integration']>

export const useOrgHasAccountingIntegration = (): AccountingIntegrationInfo => {
  const { user } = useAuthenticatedUser()
  const { data: integrations, isLoading } = useListOrganisationIntegrations({
    enabled: !user.isSupplier,
  })

  const accountingIntegration = findPrimaryAccountingIntegration(integrations ?? [])
  const hasAccountingIntegration = isDefined(accountingIntegration)

  if (!hasAccountingIntegration)
    return { hasAccountingIntegration: false, integration: null, supportsAddresses: false, isLoading }

  const integrationName = globalIntegrationsMetadata[accountingIntegration.integration].title
  const integrationType = accountingIntegration.integration as AccountingIntegration
  const supportsAddresses = ['microsoft-dynamics-fo', 'coupa'].includes(accountingIntegration.integration) // TODO: add for NetSuite and Workday when we have support

  return {
    hasAccountingIntegration,
    integration: { name: integrationName, type: integrationType },
    supportsAddresses,
    isLoading,
  }
}

export const useOwningAccountingIntegration = (
  organisationIntegrationId: string | null
): { integration: AccountingIntegrationRef | null; isLoading: boolean } => {
  const { user } = useAuthenticatedUser()
  const { data: integrations = [], isLoading } = useListOrganisationIntegrations({
    enabled: !user.isSupplier,
  })

  if (organisationIntegrationId == null) {
    const primary = findPrimaryAccountingIntegration(integrations)
    return {
      integration: toAccountingIntegrationRef(primary),
      isLoading,
    }
  }

  const owner = integrations.find(({ id }) => id === organisationIntegrationId)
  return {
    integration: toAccountingIntegrationRef(owner, owner?.name),
    isLoading,
  }
}

const findPrimaryAccountingIntegration = (integrations: OrganisationIntegration[]): OrganisationIntegration | undefined => {
  const accountingIntegrations = integrations.filter(({ integration }) => isAccountingIntegration(integration))
  return accountingIntegrations.find(({ isPrimary }) => isPrimary) ?? accountingIntegrations[0]
}

const toAccountingIntegrationRef = (
  integration: OrganisationIntegration | undefined,
  name?: string | null
): AccountingIntegrationRef | null => {
  if (integration == null || !isAccountingIntegration(integration.integration)) {
    return null
  }

  return {
    name: name ?? globalIntegrationsMetadata[integration.integration].title,
    type: integration.integration,
  }
}

/** The commit blocks that push to an ERP; each binds its own procedure on a custom integration. */
export type AccountingCommitTask = 'supplier' | 'purchaseOrder'

const boundProcedureTemplateId = (
  customAccountingIntegration: CustomAccountingIntegration | null | undefined,
  task: AccountingCommitTask
): string | null | undefined =>
  task === 'supplier'
    ? customAccountingIntegration?.commitSupplierProcedureTemplateId
    : customAccountingIntegration?.commitPurchaseOrderProcedureTemplateId

/**
 * Whether a commit block has somewhere to commit to: a native accounting integration, or a custom
 * accounting integration with a procedure bound for that commit task.
 */
export const useOrgHasAccountingIntegrationForCommitTask = (
  task: AccountingCommitTask
): { hasCommitTarget: boolean; isLoading: boolean } => {
  const { hasAccountingIntegration, isLoading: isNativeLoading } = useOrgHasAccountingIntegration()
  const { data: customAccountingIntegration, isLoading: isCustomLoading } = useGetCustomAccountingIntegration()

  return {
    hasCommitTarget: hasAccountingIntegration || isDefined(boundProcedureTemplateId(customAccountingIntegration, task)),
    isLoading: isNativeLoading || isCustomLoading,
  }
}

export const useOrgSupportsAddresses = (): { supportsAddresses: boolean; isLoading: boolean } => {
  const { supportsAddresses: nativeSupportsAddresses, isLoading: isNativeLoading } = useOrgHasAccountingIntegration()
  const { data: customAccountingIntegration, isLoading: isCustomLoading } = useGetCustomAccountingIntegration()

  return {
    supportsAddresses: nativeSupportsAddresses || isDefined(customAccountingIntegration),
    isLoading: isNativeLoading || isCustomLoading,
  }
}
